import React from "react";
import {
  Box,
  Container,
  Heading,
  Icon,
  Text,
  Stack,
  HStack,
  VStack,
  useBreakpointValue,
  Flex,
  Image
} from '@chakra-ui/react'
//import { Fa1, Fa2, Fa3, Fa4, Fa5 } from "react-icons/fa6";
import { GoDotFill } from "react-icons/go";
import { keyframes } from '@emotion/react';
import { motion } from 'framer-motion';

const glowPulse = keyframes`
  0% { filter: drop-shadow(0px 0px 12px rgba(255, 215, 0, 0.3)); }
  50% { filter: drop-shadow(0px 0px 36px rgba(255, 215, 0, 0.7)); }
  100% { filter: drop-shadow(0px 0px 12px rgba(255, 215, 0, 0.3)); }
`;

// Ajout de l'animation bounce
const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-15px); }
  60% { transform: translateY(-7px); }
`;

const listCheck = [
  {
    id: 1,
    title: 'Inclusion financière',
    text: 'Nous ciblons les populations non bancarisées et sous-bancarisées pour leur offrir des solutions adaptées à leurs besoins.',
  },
  {
    id: 2,
    title: 'Réduction de l’usage du cash',
    text: 'Grâce à nos outils digitaux, nous facilitons les paiements et transferts d’argent sans dépendre des espèces.',
  },
  {
    id: 3,
    title: 'Interconnexion régionale',
    text: 'Nous opérons au sein des pays de l’Afrique de l’Ouest pour offrir une expérience bancaire sans frontières.',
  },
]

const listCount1 = [
  {
    id: 1,
    //icon: <Fa1 />,
    title: 'Portefeuille mobile',
    text: 'Gère ton argent efficacement, tout en réduisant la dépendance aux espèces.',
  },
  {
    id: 2,
    //icon: <Fa2 />,
    title: 'Paiement par QR code',
    text: 'Scanne et paie instantanément avec des transactions sécurisées.',
  },
  {
    id: 3,
    //icon: <Fa3 />,
    title: 'Transferts et paiements',
    text: 'Effectue des envois et des réceptions d’argent à des tarifs compétitifs.',
  },
  {
    id: 4,
    //icon: <Fa4 />,
    title: 'Paiements marchands',
    text: 'Offres des solutions pour simplifier les paiements entre clients et commerçants.',
  },
]

const listCount2 = [
  {
    id: 1,
    //icon: <Fa1 />,
    title: 'Tu veux gérer ton argent sans contraintes',
    text: 'Avec Cicabank, finis les longues files d’attente en agence et effectue toutes tes opérations bancaires directement depuis ton téléphone, où que tu sois.', 
  },
  {
    id: 2,
    //icon: <Fa2 />,
    title: 'Tu cherches à réduire ta dépendance au cash',
    text: 'Nous te proposons des solutions pratiques comme les paiements par QR code et les transferts d’argent instantanés, pour que tu puisses te libérer des espèces tout en restant connecté financièrement.',
  },
  {
    id: 3,
    //icon: <Fa3 />,
    title: 'Tu veux un service sécurisé et fiable',
    text: 'Chez Cicabank, la sécurité de tes transactions est notre priorité. Avec des technologies avancées de cryptage et une authentification à deux facteurs, tu peux gérer ton argent en toute sérénité.',
  },

  {
    id: 4,
    //icon: <Fa4 />,
    title: 'Tu es non bancarisé ou sous-bancarisé',
    text: 'Cicabank est là pour toi. Nous avons créé des services spécialement conçus pour les personnes sans accès facile aux banques traditionnelles, afin de favoriser l’inclusion financière.',
  },
  {
    id: 5,
    //icon: <Fa5 />,
    title: 'Tu veux une banque régionale et connectée',
    text: 'Que tu sois en Côte d’Ivoire, au Bénin ou ailleurs en Afrique de l’Ouest, Cicabank t’offre une expérience bancaire homogène et sans frontières.',
  },
]

export default function About() {
  const headingFontSize = useBreakpointValue({ base: '2xl', md: '4xl' });
  const textFontSize = useBreakpointValue({ base: 'md', md: 'xl' });
  const sectionSpacing = useBreakpointValue({ base: 2, md: 4 });
  const containerSpacing = useBreakpointValue({ base: 4, md: 15 });

  // Wrapper motion pour Chakra UI
  const MotionHStack = motion(HStack);

  return (
    <Box p={4} mt={containerSpacing} bgColor={'#F9F9F9'}>
      <Stack spacing={4} as={Container} maxW={'6xl'} textAlign={'center'} >
        <Box 
          w={useBreakpointValue({ base: '120px', md: '150px', lg: '180px' })}
          mt={useBreakpointValue({ base: 6, md: 8 })}
          alignSelf={'center'}>       
          <Image 
            src="/assets/images/logo.svg" 
            alt="Cicabank Logo"
            filter="drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.5))"
            animation={`${glowPulse} 3s infinite ease-in-out`}
          />
        </Box>
        <Heading as='h1' fontSize={headingFontSize}>Cicabank</Heading>
        <Text color={'black'} fontSize={textFontSize}>
          Effectue tes opérations bancaires partout, à tout moment – sans jamais aller en agence !
        </Text>
      </Stack>

      <Container maxW={'6xl'} mt={10}>
        <VStack align={'start'}>
          <Text as='b' color={'#d6b601'} fontSize={headingFontSize}>Aperçu général de Cicabank</Text>
          <Text as='b' color={'black'} fontSize={textFontSize}>Cicabank : Réinventons la banque en Afrique de l’Ouest</Text>
          <Text color={'black'} fontSize={textFontSize}>
            Cicabank est conçu pour répondre aux besoins de ceux qui veulent une banque simple, accessible et moderne, adaptée à la réalité de l’Afrique de l’Ouest.
          </Text>
          <Text as='b' color={'#d6b601'} fontSize={headingFontSize} mt={sectionSpacing}>Objectif de Cicabank :</Text>
          {/* Animation des items alternée */}
          {listCheck.map((item, index) => (
            <MotionHStack 
              key={item.id} 
              align={'top'}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
            >
              <Box color={'#d6b601'} px={2} mt={1}>
                <Icon as={GoDotFill} />
              </Box>
              <VStack align={'start'}>
                <Text fontSize={textFontSize} fontWeight={600}>{item.title}</Text>
                <Text fontSize={textFontSize} color={'black'}>{item.text}</Text>
              </VStack>
            </MotionHStack>
          ))}
          <Text as='b' color={'#d6b601'} fontSize={headingFontSize} mt={sectionSpacing}>Services phares :</Text>
          {/* Animation des items alternée */}
          {listCount1.map((item, index) => (
            <MotionHStack 
              key={item.id} 
              align={'top'}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
            >
              <Box color={'#d6b601'} px={2} mt={1}>
                <Icon as={GoDotFill} />
              </Box>
              <VStack align={'start'}>
                <Text fontSize={textFontSize} fontWeight={600}>{item.title}</Text>
                <Text fontSize={textFontSize} color={'black'}>{item.text}</Text>
              </VStack>
            </MotionHStack>
          ))}
          <Text as='b' color={'#d6b601'} fontSize={headingFontSize} mt={sectionSpacing}>Notre vision :</Text>            
          <Text color={'black'} fontSize={textFontSize}>
            Cicabank vise à révolutionner le paysage bancaire de l’Afrique de l’Ouest en bâtissant un écosystème financier plus inclusif et équitable. Avec une technologie de pointe et un engagement pour la sécurité, nous aspirons à devenir un catalyseur de croissance économique et de développement social.
          </Text>
        </VStack>
        <VStack align={'start'}>
          <Text as='b' color={'#d6b601'} fontSize={headingFontSize} mt={useBreakpointValue({ base: 5, md: 10 })}>Pourquoi choisir Cicabank ?</Text>
          <Text color={'black'} fontSize={textFontSize}>
            Cicabank est conçu pour répondre aux besoins de ceux qui veulent une banque simple, accessible et moderne, adaptée à la réalité de l’Afrique de l’Ouest. Voici pourquoi Cicabank pourrait transformer ta gestion financière :
          </Text>
          {/* Animation des items alternée */}
          {listCount2.map((item, index) => (
            <MotionHStack 
              key={item.id} 
              align={'top'}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
            >
              <Box color={'#d6b601'} px={2} mt={1}>
                <Icon as={GoDotFill} />
              </Box>
              <VStack align={'start'}>
                <Text fontSize={textFontSize} fontWeight={600}>{item.title}</Text>
                <Text fontSize={textFontSize} color={'black'}>{item.text}</Text>
              </VStack>
            </MotionHStack>
          ))}
          <Text color={'black'} fontSize={textFontSize} mt={sectionSpacing}>
            Cicabank, c’est plus qu’une banque : c’est une solution pour simplifier ta vie financière, tout en t’offrant la liberté et l’autonomie dont tu as besoin.
          </Text>
          <Text color={'black'} fontSize={textFontSize} mb={sectionSpacing}>
            Alors, pourquoi attendre ? Cicabank est pour toi, parce qu’elle est pensée pour répondre à tes besoins.
          </Text>
        </VStack>
        <Flex justify={'center'} align="center" mt={useBreakpointValue({ base: 5, md: 10 })}>
          <Box 
            w={useBreakpointValue({ base: '40px', md: '50px', lg: '60px' })}
            mb={useBreakpointValue({ base: 2, md: 5 })} 
            animation={`${bounce} 2s infinite ease-in-out`}>
            <Image 
              src="/assets/images/logo.svg" 
              alt="Cicabank Logo"
              // filter="drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.5))"
            />
          </Box>
          <Text as='b' color={'black'} fontSize={textFontSize} textAlign={'center'} mb={useBreakpointValue({ base: 2, lg: 5 })}>Cicabank : La banque de demain, dès aujourd’hui.</Text>
        </Flex>
      </Container>
    </Box>
  )
}